import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs 
} from 'firebase/firestore';
import { db } from '../config/firebase';

class FirestoreUserService {
  constructor() {
    this.collectionName = 'replinq_users';
  }

  // Create user document when they register
  async createUserDocument(user, additionalData = {}) {
    try {
      const userDocRef = doc(db, this.collectionName, user.email);
      
      const userData = {
        uid: user.uid,
        email: user.email,
        firstName: additionalData.firstName || user.displayName?.split(' ')[0] || '',
        lastName: additionalData.lastName || user.displayName?.split(' ')[1] || '',
        displayName: user.displayName || additionalData.firstName + ' ' + additionalData.lastName || user.email,
        profileImageUrl: user.photoURL || '',
        company: additionalData.company || '',
        symplr_badge_ID: additionalData.symplr_badge_ID || '',
        intelli_badge_ID: additionalData.intelli_badge_ID || '',
        phoneNumber: user.phoneNumber || additionalData.phoneNumber || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        authProvider: additionalData.authProvider || 'email', // 'email', 'google', etc.
        isActive: true
      };



      await setDoc(userDocRef, userData);
      
      return {
        success: true,
        message: 'User document created successfully',
        data: userData
      };
    } catch (error) {
      console.error('Error creating user document:', error);
      return {
        success: false,
        message: 'Failed to create user document',
        error: error.message
      };
    }
  }

  // Get user document by email
  async getUserByEmail(email) {
    try {
      const userDocRef = doc(db, this.collectionName, email);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        return {
          success: true,
          data: { id: userDoc.id, ...userDoc.data() }
        };
      } else {
        return {
          success: false,
          message: 'User document not found'
        };
      }
    } catch (error) {
      console.error('Error getting user document:', error);
      return {
        success: false,
        message: 'Failed to get user document',
        error: error.message
      };
    }
  }

  // Get user document by UID
  async getUserByUID(uid) {
    try {
      const usersRef = collection(db, this.collectionName);
      const q = query(usersRef, where('uid', '==', uid));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        return {
          success: true,
          data: { id: userDoc.id, ...userDoc.data() }
        };
      } else {
        return {
          success: false,
          message: 'User document not found'
        };
      }
    } catch (error) {
      console.error('Error getting user document by UID:', error);
      return {
        success: false,
        message: 'Failed to get user document',
        error: error.message
      };
    }
  }

  // Update user document
  async updateUserDocument(email, updateData) {
    try {
      const userDocRef = doc(db, this.collectionName, email);
      
      // Add updatedAt timestamp
      const dataToUpdate = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(userDocRef, dataToUpdate);
      
      return {
        success: true,
        message: 'User document updated successfully',
        data: dataToUpdate
      };
    } catch (error) {
      console.error('Error updating user document:', error);
      return {
        success: false,
        message: 'Failed to update user document',
        error: error.message
      };
    }
  }

  // Check if user document exists
  async userDocumentExists(email) {
    try {
      const userDocRef = doc(db, this.collectionName, email);
      const userDoc = await getDoc(userDocRef);
      return userDoc.exists();
    } catch (error) {
      console.error('Error checking user document:', error);
      return false;
    }
  }

  // Get or create user document (useful for Google sign-in)
  async getOrCreateUserDocument(user, additionalData = {}) {
    try {
      // First try to get existing document
      const existingUser = await this.getUserByEmail(user.email);
      
      if (existingUser.success) {
        // User exists, but let's update their profile image if it's different
        const userData = existingUser.data;
        if (user.photoURL && userData.profileImageUrl !== user.photoURL) {
          await this.updateUserDocument(user.email, {
            profileImageUrl: user.photoURL,
            displayName: user.displayName || userData.displayName,
            updatedAt: new Date().toISOString()
          });
          
          // Return updated data
          return {
            success: true,
            data: {
              ...userData,
              profileImageUrl: user.photoURL,
              displayName: user.displayName || userData.displayName,
              updatedAt: new Date().toISOString()
            }
          };
        }
        return existingUser;
      } else {
        // Create new document if it doesn't exist
        return await this.createUserDocument(user, additionalData);
      }
    } catch (error) {
      console.error('Error in getOrCreateUserDocument:', error);
      return {
        success: false,
        message: 'Failed to get or create user document',
        error: error.message
      };
    }
  }

  // Update profile image URL
  async updateProfileImage(email, imageUrl) {
    return await this.updateUserDocument(email, { profileImageUrl: imageUrl });
  }

  // Update user profile
  async updateUserProfile(email, profileData) {
    const allowedFields = [
      'firstName', 
      'lastName', 
      'displayName', 
      'phoneNumber', 
      'company', 
      'symplr_badge_ID', 
      'intelli_badge_ID'
    ];

    // Filter to only allowed fields
    const filteredData = {};
    Object.keys(profileData).forEach(key => {
      if (allowedFields.includes(key)) {
        filteredData[key] = profileData[key];
      }
    });

    return await this.updateUserDocument(email, filteredData);
  }
}

export default FirestoreUserService; 